import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { API } from "@/App";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSession } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      toast.error("Invalid reset link");
      navigate("/login", { replace: true });
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/auth/reset-password`,
        { token, password },
        { withCredentials: true }
      );
      setSession(res.data.user, res.data.token);
      toast.success("Password updated. Welcome back!");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (!token) return null;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 relative z-10">
      <Card className="w-full max-w-md glass border-white/10" data-testid="reset-password-card">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src="/logo.png" alt="Sacred Celestial Flow" className="w-20 h-20 object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">Create a new password</CardTitle>
          <CardDescription className="text-mystic-200/70">
            Choose a strong password you'll remember.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-mystic-100">New password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-mystic-300" />
                <Input
                  id="password"
                  type="password"
                  data-testid="reset-password-new-input"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-mystic-900/50 border-white/10 focus:border-mystic-300 text-white placeholder:text-white/30"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-mystic-100">Confirm password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-mystic-300" />
                <Input
                  id="confirm"
                  type="password"
                  data-testid="reset-password-confirm-input"
                  placeholder="Repeat password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="pl-10 bg-mystic-900/50 border-white/10 focus:border-mystic-300 text-white placeholder:text-white/30"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="reset-password-submit-btn"
              disabled={loading}
              className="w-full bg-mystic-300 hover:bg-mystic-200 text-white font-medium py-3 rounded-full shadow-[0_0_15px_rgba(157,78,221,0.4)] transition-all duration-300"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update password"}
            </Button>
          </form>

          <p className="text-center text-mystic-200/70 text-sm">
            Remembered it?{" "}
            <Link to="/login" className="text-mystic-300 hover:text-mystic-200 transition-colors">
              Back to sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
